/**
 * Configuration Service
 * Platform API wrappers for rule_fragments (hierarchical config system).
 * All calls go to /platform/v1/config/*.
 */

import {
  idempotencyHeaders,
  platformDelete,
  platformErrorMessage,
  platformGet,
  platformPost,
  platformPut,
} from '@/apps/ingestion/services/_platformClient';
import { isNoDebtTypeCohort } from '@/lib/debt-type-cohort';
import { type DomainT, Scope, type ScopeT } from '@/types';
import type { AgentConfig } from '@/types/config/agent';
import type { ApiEgressConfig } from '@/types/config/api_egress';
import type { CommercialsConfig } from '@/types/config/commercials';
import type { CommunicationsConfig } from '@/types/config/communications';
import type { ContentConfig } from '@/types/config/content';
import type { CustomerDisplayConfig } from '@/types/config/customer_display';
import type { FinancialClosureConfig } from '@/types/config/financial-helpers';
import type { GovernanceConfig } from '@/types/config/governance';
import type { IngestionConfig } from '@/types/config/ingestion';
import type { IngestionAlertConfig } from '@/types/config/ingestion_alert';
import type { IngestionApiConfig } from '@/types/config/ingestion_api';
import type { JourneyConfig } from '@/types/config/journey';
import type { PayoutsConfig } from '@/types/config/payouts';
import type { ThemeConfig } from '@/types/config/theme';
import type { components } from '@/types/platform-api';

// ---------------------------------------------------------------------------
// Domain config map (unchanged — callers use these types)
// ---------------------------------------------------------------------------

type DomainConfigMap = {
  communications: CommunicationsConfig;
  commercials: CommercialsConfig;
  content: ContentConfig;
  customer_display: CustomerDisplayConfig;
  governance: GovernanceConfig;
  journey: JourneyConfig;
  financial: FinancialClosureConfig;
  payouts: PayoutsConfig;
  ingestion: IngestionConfig;
  theme: ThemeConfig;
  decisions: Record<string, unknown>;
  reporting: Record<string, unknown>;
  connections: Record<string, unknown>;
  ingestion_alert: IngestionAlertConfig;
  ingestion_sftp: Record<string, unknown>;
  ingestion_api: IngestionApiConfig;
  api_egress: ApiEgressConfig;
  agent: AgentConfig;
};

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ConfigConstraints {
  paths: string[];
  reason: string;
  enforced_at: ScopeT;
  override_allowed: boolean;
}

/** Rule fragment as returned by the Platform API. */
export interface RuleFragment {
  id: string;
  domain: string;
  scope: string;
  rules: Record<string, unknown>;
  operator_id?: string | null;
  debt_type_id?: string | null;
  client_id?: string | null;
  account_id?: string | null;
  case_id?: string | null;
  priority?: number | null;
  active?: boolean;
  constraints?: ConfigConstraints | null;
  selector_json?: Record<string, unknown> | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Compiled configuration result from the Platform API. */
export interface CompiledConfig {
  domain: string;
  config_json: Record<string, unknown>;
  provenance: unknown[];
  policy_hash: string;
  compiled_at: string;
}

export interface SaveConfigOptions<T extends keyof DomainConfigMap = keyof DomainConfigMap> {
  domain: T;
  scope: ScopeT;
  rules: DomainConfigMap[T];
  /** When true, replace fragment rules entirely (required for scope reset to drop keys). */
  replace?: boolean;
  constraints?: ConfigConstraints;
  selectorJson?: Record<string, unknown>;
  operatorId?: string;
  debtTypeId?: string;
  clientId?: string;
  accountId?: string;
  caseId?: string;
  priority?: number;
  notes?: string;
}

// ---------------------------------------------------------------------------
// Internal Platform API response types
// ---------------------------------------------------------------------------

interface CompiledConfigResponse {
  object: 'compiled_config';
  domain: string;
  config_json: Record<string, unknown>;
  provenance: unknown[];
  policy_hash: string | null;
  compiled_at: string;
  request_id?: string;
}

interface RuleFragmentResponse {
  object: 'rule_fragment';
  id: string;
  domain: string;
  scope: string;
  rules: Record<string, unknown>;
  operator_id?: string | null;
  debt_type_id?: string | null;
  client_id?: string | null;
  account_id?: string | null;
  case_id?: string | null;
  priority?: number | null;
  active?: boolean;
  constraints?: ConfigConstraints | null;
  selector_json?: Record<string, unknown> | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface ListFragmentsResponse {
  object: 'list';
  data: RuleFragmentResponse[];
  has_more: boolean;
  next_cursor: string | null;
}

// ---------------------------------------------------------------------------
// Scope resolution: entity-ID prefix → typed query param
// ---------------------------------------------------------------------------

interface TypedScopeParams {
  client_id?: string;
  debt_type_id?: string;
  account_id?: string;
  case_id?: string;
  // operator-wide: none of the above
}

/**
 * Map a prefixed entity ID to the correct typed scope query param.
 * opr_ → operator-wide (no id param), clnt_ → client_id, dbt_ → debt_type_id,
 * acct_ → account_id, case_ → case_id.
 */
function entityIdToScopeParams(entityId: string): TypedScopeParams {
  if (entityId.startsWith('clnt_')) return { client_id: entityId };
  // The "Others" cohort is not an entity; its cases inherit the client's config, and
  // callers resolve the sentinel to a client ID before reaching here.
  if (isNoDebtTypeCohort({ id: entityId })) {
    throw new Error('The "Others" cohort has no config scope of its own — pass its client ID');
  }
  if (entityId.startsWith('dbt_') || entityId.startsWith('dtyp_')) {
    return { debt_type_id: entityId };
  }
  if (entityId.startsWith('acct_')) return { account_id: entityId };
  if (entityId.startsWith('case_')) return { case_id: entityId };
  if (entityId.startsWith('opr_')) return {};
  throw new Error(`Unknown entity ID prefix for compiled config: ${entityId}`);
}

/** Map a ScopeT + ownerId to typed query params for the fragments list endpoint. */
function scopeOwnerToParams(scope: ScopeT, ownerId?: string): Record<string, string | undefined> {
  switch (scope) {
    case Scope.OPERATOR:
      // Operator scope is implied from JWT; Platform API list/compile does not take operator_id.
      return {};
    case Scope.DEBT_TYPE:
      return { debt_type_id: ownerId };
    case Scope.CLIENT:
      return { client_id: ownerId };
    case Scope.ACCOUNT:
      return { account_id: ownerId };
    case Scope.CASE:
      return { case_id: ownerId };
    default:
      return {};
  }
}

interface ScopeOwnerIds {
  operatorId?: string;
  debtTypeId?: string;
  clientId?: string;
  accountId?: string;
  caseId?: string;
}

/** The single owner ID field each scope is keyed by. Operator scope is implied from the JWT. */
const SCOPE_OWNER_FIELD: Partial<Record<ScopeT, keyof ScopeOwnerIds>> = {
  [Scope.DEBT_TYPE]: 'debtTypeId',
  [Scope.CLIENT]: 'clientId',
  [Scope.ACCOUNT]: 'accountId',
  [Scope.CASE]: 'caseId',
};

/** Owner IDs narrower than a given scope; passing one alongside it is a caller mix-up. */
const SCOPE_DESCENDANT_FIELDS: Partial<Record<ScopeT, (keyof ScopeOwnerIds)[]>> = {
  [Scope.OPERATOR]: ['debtTypeId', 'clientId', 'accountId', 'caseId'],
  [Scope.CLIENT]: ['debtTypeId', 'accountId', 'caseId'],
  [Scope.DEBT_TYPE]: ['accountId', 'caseId'],
  [Scope.ACCOUNT]: ['caseId'],
};

const OWNER_FIELD_TO_PARAM: Record<keyof ScopeOwnerIds, string> = {
  operatorId: 'operator_id',
  debtTypeId: 'debt_type_id',
  clientId: 'client_id',
  accountId: 'account_id',
  caseId: 'case_id',
};

/**
 * Owner ID for a fragment write body, keyed by scope.
 *
 * The Hub selects a debt type *within* a client, so callers hold both IDs and pass
 * both. The API keys a fragment by exactly one owner and rejects the others
 * ("scope 'debt_type' does not accept 'client_id'"), so send only the one the
 * scope is keyed by.
 */
function scopeOwnerBody(scope: ScopeT, ids: ScopeOwnerIds): Record<string, string> {
  const field = SCOPE_OWNER_FIELD[scope];
  if (!field) return {};
  const value = ids[field];
  return value === undefined ? {} : { [OWNER_FIELD_TO_PARAM[field]]: value };
}

/**
 * "Others" stands for `debt_type_id IS NULL`, not a row in `debt_types`, so it holds
 * no config of its own — those cases read the client's, and keying a fragment by the
 * sentinel would violate the debt_type_id foreign key.
 *
 * Callers are expected to resolve it away first (`configScopeIds`), which is what
 * lands such a write on the client. This throws rather than resolving it here, so a
 * caller that forgets fails loudly instead of writing to a scope it did not name.
 */
function assertNotNoDebtTypeCohort(ids: ScopeOwnerIds): void {
  if (ids.debtTypeId && isNoDebtTypeCohort({ id: ids.debtTypeId })) {
    throw new Error('The "Others" cohort has no config of its own — save against its client instead');
  }
}

// ---------------------------------------------------------------------------
// Config API: compiled read
// ---------------------------------------------------------------------------

/**
 * Fetch compiled (hierarchically merged) configuration for an entity.
 *
 * @param entityId - Prefixed entity ID (opr_, clnt_, dbt_, acct_, case_).
 *                   The prefix determines which scope param is sent to the API.
 * @param domain   - Optional domain filter.
 */
export async function getCompiledConfig(entityId: string, domain?: DomainT): Promise<CompiledConfig | null> {
  if (!domain) return null;

  const scopeParams = entityIdToScopeParams(entityId);

  const { data, error } = await platformGet('/platform/v1/config/compiled', {
    params: {
      query: {
        domain,
        ...scopeParams,
      },
    },
  });

  if (error) {
    console.error('[getCompiledConfig] Error:', platformErrorMessage(error, 'Failed to fetch compiled config'));
    throw new Error(platformErrorMessage(error, 'Failed to fetch compiled config'));
  }

  if (!data) return null;

  const result = data as CompiledConfigResponse;

  return {
    domain: result.domain,
    config_json: result.config_json,
    provenance: result.provenance ?? [],
    policy_hash: result.policy_hash ?? '',
    compiled_at: result.compiled_at,
  };
}

// ---------------------------------------------------------------------------
// Config API: fragment writes
// ---------------------------------------------------------------------------

/**
 * Save or update a configuration fragment.
 * Defaults to replace:true (full fragment writes).
 * Pass replace:false only when intentionally patching a subset of keys.
 */
export async function saveConfig<T extends keyof DomainConfigMap>(options: SaveConfigOptions<T>): Promise<RuleFragment> {
  const {
    domain,
    scope,
    rules,
    replace = true,
    constraints,
    selectorJson,
    operatorId,
    debtTypeId,
    clientId,
    accountId,
    caseId,
    priority = 100,
    notes,
  } = options;

  const ownerIds = { operatorId, debtTypeId, clientId, accountId, caseId };
  assertNotNoDebtTypeCohort(ownerIds);

  validateScopeConstraints(scope, ownerIds);

  if (constraints && scope !== Scope.OPERATOR && scope !== Scope.SYSTEM) {
    throw new Error('Only operator and system scopes can define constraints');
  }

  const body: Record<string, unknown> = {
    domain,
    scope,
    rules,
    replace,
    priority,
    ...scopeOwnerBody(scope, ownerIds),
    ...(selectorJson !== undefined ? { selector_json: selectorJson } : {}),
    ...(constraints !== undefined ? { constraints } : {}),
    ...(notes !== undefined ? { notes } : {}),
  };

  const { data, error } = await platformPost('/platform/v1/config/fragments', {
    body,
    headers: idempotencyHeaders(),
  });

  if (error) {
    console.error('[saveConfig] Error:', platformErrorMessage(error, 'Failed to save config'));
    throw new Error(platformErrorMessage(error, 'Failed to save config'));
  }

  const result = data as RuleFragmentResponse;
  return fragmentResponseToRuleFragment(result);
}

// ---------------------------------------------------------------------------
// Config API: SFTP destination credentials (WorkOS Vault) — shared across config domains
// ---------------------------------------------------------------------------

interface SftpCredentialResponse {
  object: 'sftp_credential';
  destination_name: string;
  vault_secret_id: string;
  request_id?: string;
}

/**
 * Store an SFTP destination's secret (password or private key) in WorkOS Vault.
 * The secret never touches rule_fragments — only the returned vault_secret_id is
 * persisted, into the connections config's sftp_destinations entry.
 *
 * Pass `vaultSecretId` when rotating an existing destination's secret: the Vault
 * object is updated in place (id unchanged) so no orphaned secret is left behind.
 *
 * @returns the WorkOS Vault secret ID to store on the destination.
 */
export async function storeSftpCredential(destinationName: string, secret: string, vaultSecretId?: string): Promise<string> {
  const { data, error } = await platformPost('/platform/v1/config/sftp-credentials', {
    body: { destination_name: destinationName, secret, ...(vaultSecretId ? { vault_secret_id: vaultSecretId } : {}) },
    headers: idempotencyHeaders(),
  });

  if (error) {
    console.error('[storeSftpCredential] Error:', platformErrorMessage(error, 'Failed to store SFTP credential'));
    throw new Error(platformErrorMessage(error, 'Failed to store SFTP credential'));
  }

  return (data as SftpCredentialResponse).vault_secret_id;
}

/**
 * Delete an SFTP destination's secret from WorkOS Vault so a removed destination
 * doesn't orphan its credential. Pass the destination's vault_secret_id.
 */
export async function deleteSftpCredential(vaultSecretId: string): Promise<void> {
  const { error } = await platformDelete(`/platform/v1/config/sftp-credentials/${vaultSecretId}`, {
    headers: idempotencyHeaders(),
  });

  if (error) {
    // An already-absent secret (NOT_FOUND) means cleanup's goal is already met —
    // treat it as success so a removed-but-not-deleted secret can still be cleared.
    if ((error as { error?: { code?: string } }).error?.code === 'NOT_FOUND') return;
    console.error('[deleteSftpCredential] Error:', platformErrorMessage(error, 'Failed to delete SFTP credential'));
    throw new Error(platformErrorMessage(error, 'Failed to delete SFTP credential'));
  }
}

// ---------------------------------------------------------------------------
// Config API: Client Config Cloning
// ---------------------------------------------------------------------------

/** Options for cloning client configuration. */
export interface CloneClientConfigOptions {
  sourceClientId: string;
  targetClientId: string;
  includeDebtTypes?: boolean;
  includeDesignsTemplates?: boolean;
  includeRules?: boolean;
  includeIngestion?: boolean;
  includeEgress?: boolean;
}

/**
 * Clone client configuration from a source client to a target client.
 * Hits POST /platform/v1/config/clients/actions/clone.
 *
 * @returns Map of cloned item counts per category (e.g. { rule_fragments: 5, comm_designs: 2 })
 */
export async function cloneClientConfig(options: CloneClientConfigOptions): Promise<Record<string, number>> {
  const {
    sourceClientId,
    targetClientId,
    includeDebtTypes = true,
    includeDesignsTemplates = true,
    includeRules = true,
    includeIngestion = true,
    includeEgress = true,
  } = options;

  const body = {
    source_client_id: sourceClientId,
    target_client_id: targetClientId,
    include_debt_types: includeDebtTypes,
    include_designs_templates: includeDesignsTemplates,
    include_rules: includeRules,
    include_ingestion: includeIngestion,
    include_egress: includeEgress,
  };

  const { data, error } = await platformPost('/platform/v1/config/clients/actions/clone', {
    body,
    headers: idempotencyHeaders(),
  });

  if (error) {
    console.error('[cloneClientConfig] Error:', platformErrorMessage(error, 'Failed to clone client config'));
    throw new Error(platformErrorMessage(error, 'Failed to clone client config'));
  }

  const result = data as { counts?: Record<string, number> };
  return result?.counts ?? {};
}

// ---------------------------------------------------------------------------
// Finance API: Invoice groups
// ---------------------------------------------------------------------------

/** Clients invoiced together under the group client's invoicing settings. */
export type InvoiceGroup = components['schemas']['InvoiceGroupResponse'];

export type InvoiceGroupFrequency = components['schemas']['CreateInvoiceGroupBody']['frequency'];

/** The operator's invoice groups whose every client the caller can access. */
export async function listInvoiceGroups(): Promise<InvoiceGroup[]> {
  const { data, error } = await platformGet('/platform/v1/finance/invoice-groups', {});
  if (error) throw new Error(platformErrorMessage(error, 'Failed to load invoice groups'));
  return (data as components['schemas']['ListEnvelope_InvoiceGroupResponse_']).data;
}

/**
 * Group `clientIds` under the operator's invoicing settings, invoiced at `frequency`; every
 * client's own invoicing settings are removed. The smallest client id holds the group's settings.
 */
export async function createInvoiceGroup(clientIds: string[], frequency: InvoiceGroupFrequency, name?: string): Promise<InvoiceGroup> {
  const { data, error } = await platformPost('/platform/v1/finance/invoice-groups', {
    body: { client_ids: clientIds, frequency, name: name || null },
    headers: idempotencyHeaders(),
  });
  if (error) throw new Error(platformErrorMessage(error, 'Failed to create the invoice group'));
  return data as InvoiceGroup;
}

/**
 * Make `groupClientId`'s group exactly `clientIds` in one change. Joining removes a client's own
 * invoicing settings; a client that leaves is invoiced manually until it is set up again.
 * Resolves to the client now holding the group's settings, null once dissolved.
 */
export async function setInvoiceGroupMembers(groupClientId: string, clientIds: string[]): Promise<string | null> {
  const { data, error } = await platformPut(`/platform/v1/finance/invoice-groups/${encodeURIComponent(groupClientId)}/members`, {
    body: { client_ids: clientIds },
    headers: idempotencyHeaders(),
  });
  if (error) throw new Error(platformErrorMessage(error, 'Failed to update the invoice group'));
  return (data as components['schemas']['InvoiceGroupMembersResponse']).group_client_id ?? null;
}

// ---------------------------------------------------------------------------
// Config API: fragment reads
// ---------------------------------------------------------------------------

/**
 * Fetch a single rule fragment at the specified scope level (no hierarchy).
 * Returns null if not found.
 */
export async function getRuleFragment(domain: DomainT, scope: ScopeT, ownerId?: string): Promise<RuleFragment | null> {
  const ownerParams = scopeOwnerToParams(scope, ownerId);

  const { data, error } = await platformGet('/platform/v1/config/fragments', {
    params: {
      query: {
        domain,
        scope,
        ...ownerParams,
        limit: 1,
      },
    },
  });

  if (error) {
    console.error('[getRuleFragment] Error:', platformErrorMessage(error, 'Failed to fetch rule fragment'));
    throw new Error(platformErrorMessage(error, 'Failed to fetch rule fragment'));
  }

  if (!data) return null;

  const list = data as ListFragmentsResponse;
  const first = list.data?.[0];
  if (!first) return null;

  return fragmentResponseToRuleFragment(first);
}

// ---------------------------------------------------------------------------
// Config API: delete
// ---------------------------------------------------------------------------

/**
 * Delete a configuration fragment at the specified scope.
 * Looks up the fragment by natural key first, then issues a DELETE by ID.
 * Returns true if deleted, false if no fragment existed.
 */
export async function deleteConfig(domain: DomainT, scope: ScopeT, ownerId: string): Promise<boolean> {
  const ownerParams = scopeOwnerToParams(scope, ownerId);

  const { data: listData, error: listError } = await platformGet('/platform/v1/config/fragments', {
    params: {
      query: {
        domain,
        scope,
        ...ownerParams,
        limit: 1,
      },
    },
  });

  if (listError) {
    console.error('[deleteConfig] Error listing fragments:', platformErrorMessage(listError, 'Failed to list fragments'));
    throw new Error(platformErrorMessage(listError, 'Failed to list fragments'));
  }

  const list = listData as ListFragmentsResponse | undefined;
  const fragment = list?.data?.[0];
  if (!fragment) return false;

  const { error: deleteError } = await platformDelete(`/platform/v1/config/fragments/${fragment.id}`, {
    headers: idempotencyHeaders(),
  });

  if (deleteError) {
    console.error('[deleteConfig] Error deleting fragment:', platformErrorMessage(deleteError, 'Failed to delete fragment'));
    throw new Error(platformErrorMessage(deleteError, 'Failed to delete fragment'));
  }

  return true;
}

// ---------------------------------------------------------------------------
// Config API: operator constraints
// ---------------------------------------------------------------------------

/**
 * Fetch operator-level constraint paths for a domain.
 */
export async function getOperatorConstraints(operatorId: string, domain: DomainT): Promise<string[]> {
  const fragments = await listFragmentsByScope(domain, Scope.OPERATOR, operatorId);

  const allPaths: string[] = [];
  for (const fragment of fragments) {
    if (fragment.constraints?.paths) {
      allPaths.push(...fragment.constraints.paths);
    }
  }
  return [...new Set(allPaths)];
}

/**
 * Fetch full constraint metadata for a domain at operator scope.
 */
export async function getOperatorConstraintMetadata(operatorId: string, domain: DomainT): Promise<ConfigConstraints[]> {
  const fragments = await listFragmentsByScope(domain, Scope.OPERATOR, operatorId);
  return fragments.map((f) => f.constraints).filter((c): c is ConfigConstraints => c != null);
}

// ---------------------------------------------------------------------------
// Legacy: loadCompiledConfig
// ---------------------------------------------------------------------------

/**
 * Load compiled configuration for a specific entity and domain.
 * Delegates to getCompiledConfig after resolving the most-specific entity ID.
 */
export async function loadCompiledConfig(
  domain: string,
  operatorId?: string,
  clientId?: string,
  debtTypeId?: string
): Promise<CompiledConfig | null> {
  const entityId = debtTypeId ?? clientId ?? operatorId;
  if (!entityId) return null;
  return getCompiledConfig(entityId, domain as DomainT);
}

/**
 * No-op — materialized view refresh requires elevated DB privileges.
 * Retained for backward compat; the Platform API handles invalidation.
 */
export async function refreshCompiledConfigs(): Promise<void> {
  console.warn('refreshCompiledConfigs: handled server-side by Platform API');
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function fragmentResponseToRuleFragment(r: RuleFragmentResponse): RuleFragment {
  return {
    id: r.id,
    domain: r.domain,
    scope: r.scope,
    rules: r.rules,
    operator_id: r.operator_id ?? null,
    debt_type_id: r.debt_type_id ?? null,
    client_id: r.client_id ?? null,
    account_id: r.account_id ?? null,
    case_id: r.case_id ?? null,
    priority: r.priority ?? null,
    active: r.active,
    constraints: r.constraints ?? null,
    selector_json: r.selector_json ?? null,
    notes: r.notes ?? null,
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

export async function listFragmentsByScope(domain: DomainT, scope: ScopeT, ownerId?: string): Promise<RuleFragment[]> {
  const ownerParams = scopeOwnerToParams(scope, ownerId);

  const { data, error } = await platformGet('/platform/v1/config/fragments', {
    params: {
      query: { domain, scope, ...ownerParams },
    },
  });

  if (error) {
    console.error('[listFragmentsByScope] Error:', platformErrorMessage(error, 'Failed to list fragments'));
    throw new Error(platformErrorMessage(error, 'Failed to list fragments'));
  }

  const list = data as ListFragmentsResponse | undefined;
  return (list?.data ?? []).map(fragmentResponseToRuleFragment);
}

function validateScopeConstraints(
  scope: ScopeT,
  ids: {
    operatorId?: string;
    debtTypeId?: string;
    clientId?: string;
    accountId?: string;
    caseId?: string;
  }
): void {
  if (scope === Scope.SYSTEM) {
    if (ids.operatorId || ids.debtTypeId || ids.clientId || ids.accountId || ids.caseId) {
      throw new Error('System scope must not have any owner IDs');
    }
    return;
  }

  if (scope === Scope.OPERATOR) {
    if (!ids.operatorId) {
      throw new Error('Operator scope requires operator_id');
    }
  } else {
    const required = SCOPE_OWNER_FIELD[scope];
    if (required && !ids[required]) {
      throw new Error(`Scope '${scope}' requires ${OWNER_FIELD_TO_PARAM[required]}`);
    }
  }

  // A caller legitimately holds the ancestors of the selected scope (the Hub selects
  // a debt type within a client), so only reject IDs from *below* the scope — those
  // signal a genuine mix-up. scopeOwnerBody keeps the ancestors out of the request.
  const descendants = SCOPE_DESCENDANT_FIELDS[scope] ?? [];
  for (const field of descendants) {
    if (ids[field] !== undefined) {
      throw new Error(`Scope '${scope}' does not accept ${OWNER_FIELD_TO_PARAM[field]}`);
    }
  }
}
